'use client'

import { type KeyboardEvent, useCallback, useEffect, useRef } from 'react'
import classNames from 'classnames'
import {
  LayoutHeaderActionType,
  useLayout,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { DesktopStateActionType, SubMenu, useHeaderContext } from './header-context'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import { NavigationEventType } from '@dustin-web/microsite-components/src/utils/digital-data-types'

export const ProductsNavItems = () => {
  const { header, dispatchHeader } = useLayout()
  const { desktop, dispatchDesktop } = useHeaderContext()
  const { translate } = useTranslations()
  const firstProductNavRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (desktop.activeMegaMenu === 'category') {
      firstProductNavRef.current?.focus()
    }
  }, [desktop.activeMegaMenu])

  useEffect(() => {
    if (!desktop.activeMegaMenu && header.isProductOpen) {
      dispatchHeader({
        type: LayoutHeaderActionType.SET_ACTIVE_SUB_MENU,
        payload: { subMenu: SubMenu.PRODUCTS },
      })
    }
  }, [desktop.activeMegaMenu])

  const toggleMegaMenu = useCallback(
    (megaMenu: string) => {
      dispatchDesktop({
        type: DesktopStateActionType.SET_ACTIVE_MEGA_MENU,
        payload: {
          activeMegaMenu: desktop.activeMegaMenu === megaMenu ? '' : megaMenu,
        },
      })
    },
    [desktop.activeMegaMenu, dispatchDesktop]
  )

  const handleEnterKey = (
    event: KeyboardEvent<HTMLButtonElement>,
    megaMenu: 'category' | 'brand'
  ) => {
    if (event.key !== 'Enter') {
      return
    }

    if (desktop.activeMegaMenu === megaMenu) {
      event.preventDefault()
    }

    dispatchDesktop({
      type: DesktopStateActionType.SET_FOCUS_MEGA_MENU,
      payload: {
        focusMegaMenu: megaMenu,
      },
    })
  }

  return (
    <>
      <li key={'category-tree'} className="c-header__navigation-item">
        <button
          ref={firstProductNavRef}
          data-ga-title="our-assortment"
          data-test-id="our-assortment"
          aria-expanded={desktop.activeMegaMenu === 'category'}
          className={classNames('c-header__navigation-btn button-stripped js-mega-menu-trigger', {
            'c-header__navigation-btn--active': desktop.activeMegaMenu === 'category',
          })}
          onKeyDown={event => handleEnterKey(event, 'category')}
          onClick={() => toggleMegaMenu('category')}>
          {translate('Header_OurAssortment')}
        </button>
      </li>
      <li className="c-header__navigation-item">
        <button
          onKeyDown={event => handleEnterKey(event, 'brand')}
          data-ga-title="brands"
          aria-expanded={desktop.activeMegaMenu === 'brand'}
          className={classNames('c-header__navigation-btn button-stripped js-mega-menu-trigger', {
            'c-header__navigation-btn--active': desktop.activeMegaMenu === 'brand',
          })}
          onClick={() => {
            toggleMegaMenu('brand')
          }}>
          {translate('Header_Brands')}
        </button>
      </li>
      <li className="c-header__navigation-item">
        <a
          data-ga-title="accessory-guide"
          className="c-header__navigation-link"
          href="/accessories/guides">
          {translate('Header_AccessoryFinder')}
        </a>
      </li>
    </>
  )
}

const ProductsNavABTest = () => {
  const { dispatchDesktop } = useHeaderContext()
  const { header, dispatchHeader } = useLayout()
  const { translate } = useTranslations()

  const onProductsClick = () => {
    dispatchHeader({
      type: LayoutHeaderActionType.SET_ACTIVE_SUB_MENU,
      payload: { subMenu: SubMenu.PRODUCTS },
    })

    dispatchDesktop({
      type: DesktopStateActionType.SET_ACTIVE_MEGA_MENU,
      payload: {
        activeMegaMenu: header.isProductOpen ? '' : 'category',
      },
    })
    DigitalData.pushNavigationInteraction(
      NavigationEventType.MainMenu,
      'products',
      header.isProductOpen ? 'close_menu' : 'open_menu'
    )
  }

  return (
    <>
      <li className="c-header__navigation-item">
        <button
          data-ga-title="products"
          data-test-id="category-tree-menu"
          aria-expanded={header.isProductOpen}
          className={classNames('c-header__navigation-btn button-stripped js-mega-menu-trigger', {
            'c-header__navigation-btn--active': header.isProductOpen,
          })}
          onClick={onProductsClick}>
          {translate('Products')}
        </button>
      </li>
    </>
  )
}

export default ProductsNavABTest
